import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import { getStudents } from '@/app/services/students/students';
import * as Actions from '@/app/store/actions';
import { generateAgeString } from '@/utils/utils';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Avatar,
	CircularProgress,
	FormControl,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch } from 'react-redux';
import SingleRoomRatioStats from './RatioStats/SingleRoomRatioStats';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';

const RoomStudents = ({ room }) => {
	const dispatch = useDispatch();
	const [searchQuery, setSearchQuery] = useState('');
	const [rows, setRows] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [hasMore, setHasMore] = useState(false);
	const [firstLoad, setFirstLoad] = useState(true);
	const [fetchingMore, setFetchingMore] = useState(false);
	const [page, setPage] = useState(1);
	const { id } = room;
	const ref = useRef();
	const navigate = useNavigate();
	const [filters, setFilters] = useState({ status: 1, statusValue: 1, roomId: id, checkIn: '', absent: '' });

	const handleLoadMore = () => {
		setFetchingMore(true);
		getStudents(filters.searchQuery, filters.status, filters.roomId, filters.checkIn, page, filters.absent)
			.then((res) => {
				if (res.data.last_page > res.data.current_page) {
					setHasMore(true);
				} else {
					setHasMore(false);
				}
				setPage(res.data.current_page + 1);
				setRows(rows.concat(res.data.data));
				setFetchingMore(false);
			})
			.catch(() => {
				setFetchingMore(false);
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch data, please refresh',
						variant: 'error',
					}),
				);
			});
	};

	useEffect(() => {
		let isMounted = true;
		const timeout = setTimeout(
			() => {
				if (!isMounted) return;
				setIsLoading(true);
				getStudents(searchQuery, filters.status, filters.roomId, filters.checkIn, 1, filters.absent)
					.then((res) => {
						if (!isMounted) return;
						setFirstLoad(false);
						setRows(res.data.data);
						setHasMore(res.data.to < res.data.total);
						setPage(res.data.current_page + 1);
					})
					.catch(() => {
						if (!isMounted) return;
						dispatch(
							Actions.showMessage({
								message: 'Failed to fetch data, please refresh',
								variant: 'error',
							}),
						);
					})
					.finally(() => {
						setIsLoading(false);
					});
			},
			firstLoad ? 0 : 1000,
		);
		return () => {
			isMounted = false;
			clearTimeout(timeout);
		};
	}, [filters, searchQuery]);

	const handleFilters = (ev) => {
		if (ev.target.name === 'searchQuery') {
			setFilters({ ...filters, searchQuery: ev.target.value });
		}

		if (ev.target.name === 'filter') {
			if (ev.target.value === 1) {
				setFilters({ ...filters, status: 1, checkIn: '', absent: '', statusValue: ev.target.value });
			} else if (ev.target.value === 2) {
				setFilters({ ...filters, status: 0, checkIn: '', absent: '', statusValue: ev.target.value });
			} else if (ev.target.value === 3) {
				setFilters({ ...filters, status: '', checkIn: 1, absent: '', statusValue: ev.target.value });
			} else if (ev.target.value === 4) {
				setFilters({ ...filters, status: '', checkIn: '', absent: 1, statusValue: ev.target.value });
			}
		}

		if (ev.target.name === 'room') {
			if (ev.target.value) {
				setFilters({ ...filters, roomId: ev.target.value });
			} else {
				setFilters({ ...filters, roomId: '' });
			}
		}
	};
	const downloadQRCode = async () => {
		const element = ref.current;
		const canvas = await html2canvas(element);

		const data = canvas.toDataURL('image/jpg');
		const link = document.createElement('a');

		if (typeof link.download === 'string') {
			link.href = data;
			link.download = `${room.name}-QR-Code.jpg`;

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else {
			window.open(data);
		}
	};

	const openQRCode = () => {
		dispatch(
			Actions.openDialog({
				children: (
					<div className="p-32 flex flex-col items-center gap-y-4">
						<div className="self-end">
							<i
								className="fas text-[20px]"
								onClick={() => dispatch(Actions.closeDialog())}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										dispatch(Actions.closeDialog());
									}
								}}
								role="button"
								tabIndex={0}
							/>
						</div>
						<span ref={ref}>
							<QRCodeSVG height={300} id="qrcode-svg" includeMargin value={String(id)} width={300} />
						</span>
						<CustomButton height={33} onClick={downloadQRCode} variant="primary" width={120}>
							Download
						</CustomButton>
					</div>
				),
			}),
		);
	};

	return (
		<div className="mt-32">
			<div className="mx-auto room-width flex flex-col gap-16">
				<div className="flex items-center  flex-nowrap justify-between">
					<span className="flex self-end">
						{' '}
						<span className="">
							<IconButton
								className="h-44 w-44"
								onClick={() => {
									navigate('/Rooms');
								}}
							>
								<img alt="filter" className="" height="24px" src="assets/images/arrow-long.png" width="24px" />
							</IconButton>
						</span>{' '}
						<span className="text-pd-2xl  font-bold mr-8 text-room" style={{ marginTop: '4px' }}>
							{room.name}
						</span>
						<QRCodeSVG className="cursor-pointer" height={30} onClick={openQRCode} value={String(room.id)} width={30} />
					</span>
					<div className="flex justify-between">
						<div className="flex">
							<TextField
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												className="p-6"
												onClick={() => {
													document.getElementById('search').focus();
												}}
											>
												<img alt="search-icon" height="15px" src="assets/images/search-icon.svg" width="15px" />
											</IconButton>
										</InputAdornment>
									),
								}}
								className="name-height"
								id="search"
								label="Search By Name"
								onChange={(ev) => setSearchQuery(ev.target.value)}
								style={{ width: 182 }}
								value={searchQuery}
							/>
							<div className="mx-8">
								<FormControl>
									<InputLabel id="filterLabel">Filter</InputLabel>
									<Select
										endAdornment={
											filters.statusValue ? (
												<IconButton
													className="mr-16"
													onClick={() =>
														setFilters({
															...filters,
															status: '',
															checkIn: '',
															statusValue: '',
															absent: '',
														})
													}
													size="small"
												>
													<Close fontSize="small" />
												</IconButton>
											) : (
												''
											)
										}
										id="filter"
										label="Filter"
										labelId="filterlabel"
										name="filter"
										onChange={handleFilters}
										style={{ width: 166, height: 34 }}
										value={filters.statusValue}
									>
										<MenuItem value={1}>
											<span id="active"> Active</span>
										</MenuItem>
										<MenuItem value={2}>
											<span id="inactive"> Inactive</span>
										</MenuItem>
										<MenuItem value={3}>
											<span id="check-in"> Checked-In</span>
										</MenuItem>
										<MenuItem value={4}>
											<span id="check-out"> Checked-Out</span>
										</MenuItem>
									</Select>
								</FormControl>
							</div>
						</div>
						<div className="self-end">
							<span>
								<span className="mx-4">
									<CustomButton
										fontSize="15px"
										height="43"
										id="go-to-room-settings"
										onClick={() => {
											navigate(`/rooms-roomsetting/${room.id}`, { state: { row: room } });
										}}
										variant="secondary"
										width="140px"
									>
										<FontAwesomeIcon icon={faCog} /> {room?.room_type === 'place' ? 'Settings' : 'Room Settings'}
									</CustomButton>
								</span>
								<span className="mx-4">
									{room?.room_type == 'room' ? (
										<CustomButton
											fontSize="15px"
											height="43"
											onClick={() => {
												navigate(`/rooms-roomschedule/${room.id}`, { state: { row: room } });
											}}
											variant="primary"
											width="140px"
										>
											Room Schedules
										</CustomButton>
									) : null}
								</span>
							</span>
						</div>
					</div>
				</div>
				{room?.room_type !== 'place' ? <SingleRoomRatioStats roomData={room} /> : null}
				<TableContainer className=" p-[24px] pt-0 mb-[162px] h-[60vh]" component={Paper} id="Scrollable-table">
					<Table className="student-table" stickyHeader style={{ width: '100%' }}>
						<TableHead>
							<TableRow>
								<TableCell className="bg-white studentTableHeader" style={{ width: '30%' }}>
									Student
								</TableCell>
								<TableCell className="bg-white studentTableHeader" style={{ width: '45%' }}>
									Parents
								</TableCell>
								<TableCell className="bg-white studentTableHeader approve-hd " style={{ width: '25%' }}>
									Authorized Contacts
								</TableCell>
							</TableRow>
						</TableHead>

						<TableBody className="">
							{isLoading ? (
								<TableRow>
									<TableCell align="center" colSpan={8}>
										<CircularProgress size={35} />
									</TableCell>
								</TableRow>
							) : !rows.length && !firstLoad ? (
								<TableRow>
									<TableCell align="center" colSpan={8}>
										No Students
									</TableCell>
								</TableRow>
							) : (
								rows?.map((row) => {
									return (
										<TableRow key={row.id}>
											<TableCell component="th" scope="row" style={{ fontWeight: 700 }}>
												<div className="flex ">
													<Avatar className="mr-6" src={row?.thumb || row?.photo} />
													<div className="flex flex-col items-start">
														<div className="truncate break-word std-name">
															{row.first_name} {row.last_name}
														</div>
														<div className="student-age-room truncate">{generateAgeString(row.date_of_birth)}</div>
													</div>
												</div>
											</TableCell>

											<TableCell style={{ fontWeight: 700 }}>
												<div className="grid grid-cols-2 auto-col-min auto-row-min">
													<div className="flex items-center cursor-pointer">
														<Avatar className="mr-4" src={row?.parents[0]?.thumb} />
														<div className="flex flex-col">
															<div className="parent-name-room truncate">
																{row?.parents[0]?.first_name} {row?.parents[0]?.last_name}
															</div>
															<div className="parent-relation-room self-start truncate">
																{row?.parents[0]?.relation_with_child}
															</div>
														</div>
													</div>
													{row.parents.length > 1 ? (
														<div className="flex items-center cursor-pointer">
															<Avatar className="mr-4" src={row.parents[1]?.thumb} />
															<div className="flex flex-col items-center">
																<div className="parent-name-room truncate">
																	{row.parents[1]?.first_name} {row.parents[1]?.last_name}
																</div>
																<div className="parent-relation-room self-start truncate">
																	{row.parents[1]?.relation_with_child}
																</div>
															</div>
														</div>
													) : (
														''
													)}
												</div>
											</TableCell>

											<TableCell style={{ fontWeight: 700 }}>
												<div className="flex overflow-auto">
													{row?.approved_pickups?.concat(row?.extended_family)?.map((family) => {
														return (
															<Tooltip
																key={family.id}
																title={
																	<div className="flex flex-col items-center">
																		<div>
																			{family.first_name} {family.last_name}
																		</div>
																		<div className="mt-2">{family.relation_with_child}</div>
																	</div>
																}
															>
																<Avatar className="mx-4 cursor-pointer" src={family.thumb} />
															</Tooltip>
														);
													})}
												</div>
											</TableCell>

											<TableCell style={{ fontWeight: 700 }} />
										</TableRow>
									);
								})
							)}
							{fetchingMore ? (
								<TableRow>
									<TableCell align="center" colSpan={8}>
										<CircularProgress size={35} />
									</TableCell>
								</TableRow>
							) : null}
						</TableBody>
					</Table>
				</TableContainer>
				<InfiniteScroll
					dataLength={rows.length}
					hasMore={hasMore}
					next={handleLoadMore}
					scrollableTarget="Scrollable-table"
				/>
			</div>
		</div>
	);
};

export default RoomStudents;
